import { useDispatch, useSelector } from "react-redux"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { SystemDescriptions } from "src/common/descriptions"
import { PayableContractDebit } from "src/common/models/payableContractDebit"
import { PageCard } from "src/common/styles/styles"
import { SearchableTable } from "src/components/SearchableTable"
import { RootState } from "src/state/reducer"
import { GET_PAYABLE_CONTRACT_DEBITS_API, GET_PAYABLE_DEBIT_BY_ID_API } from "../state/actionTypes"
import { ColumnsType } from "antd/es/table"
import { ColumnsKeys, summaryColumns, TABLE_COLUMNS } from "./config"
import { useEffect, useState } from "react"
import { cleanCurrentPayableDebit, getPayableContractDebitsApi, getPayableDebitByIdApi } from "../state/action"
import { ServiceDebitStatus } from "src/common/models/serviceContractDebit"
import { searchableTableUtils } from "src/components/SearchableTable/utils"
import { Button, Space, Tooltip, notification } from "antd"
import { CreditCardOutlined, DeleteOutlined } from '@ant-design/icons'
import { Loader } from "src/components/Loader"
import { CreatePayableContractPayment } from "src/features/PayableContract/CreatePayableContractPayment"
import useUserPermission from "src/hooks/useUserPermissions"
import { USER_SCOPES } from "src/hooks/UserPermission"
import { createAuthTokenApi } from "src/features/Security/state/action"
import { CREATE_AUTH_TOKEN_API } from "src/features/Security/state/actionTypes"
import { AuthTokenValidator } from "src/components/AuthTokenValidator"
import { AuthTokenRequest } from "src/common/models/auth"
import { ContractDebitDelete } from "src/features/PayableContract/ContractDebitDelete"

export type ReduxProps = {
    payableContractDebits: PayableContractDebit[],
    isGettingContractDebits: boolean
    currentPaymentDebit?: PayableContractDebit
    isGettingContractDebit: boolean
    createAuthTokenSuccess: boolean
    isCreatingAuthToken: boolean
    pageToken?: string
}

export const PayableContractDebits = () => {
    const dispatch = useDispatch()
    // const navigate = useNavigate()

    const { validateUserScopeForPage } = useUserPermission()

    const [selectedAction, setSelectedAction] = useState<"DELETE_PCD" | undefined>(undefined)
    const [refId, setRefId] = useState<string | undefined>(undefined)
    const [tokenValidatorOpen, setTokenValidatorOpen] = useState<boolean>(false)
    const [payableContractToDelete, setPayableContractToDelete] = useState<PayableContractDebit | undefined>(undefined)

    const descriptions = SystemDescriptions.PAGES.PAYABLE_CONTRACT.PAYABLE_CONTRACT_DEBITS
    const commonDescriptions = SystemDescriptions.PAGES.COMMON
    const createDescriptions = SystemDescriptions.PAGES.CONTRACT_PAYMENT.CREATE_PAYMENT
    const deleteDescriptions = SystemDescriptions.PAGES.PAYABLE_CONTRACT.DELTE_CONTRACT_DEBIT_RECORD

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        payableContractDebits: state.accountPayables.payableContractDebits || [],
        isGettingContractDebits: serviceLoading(state, [GET_PAYABLE_CONTRACT_DEBITS_API]),
        currentPaymentDebit: state.accountPayables.currentPayableDebit,
        isGettingContractDebit: serviceLoading(state, [GET_PAYABLE_DEBIT_BY_ID_API]),
        createAuthTokenSuccess: state.security.createAuthTokenSuccess,
        isCreatingAuthToken: serviceLoading(state, [CREATE_AUTH_TOKEN_API]),
        pageToken: state.security.pageToken,
    }))

    const initData = () => {
        dispatch(getPayableContractDebitsApi({
            status: ServiceDebitStatus.PENDING_PAYMENT,
        }))
    }

    useEffect(() => {
        validateUserScopeForPage(USER_SCOPES.PAYABLE_CONTRACT_DEBIT_READ)
        initData()
    }, [])

    useEffect(() => {
        if (reduxProps.createAuthTokenSuccess && selectedAction === "DELETE_PCD") {
            setTokenValidatorOpen(true)
        }
    }, [reduxProps.createAuthTokenSuccess])

    const tblColumns: ColumnsType<PayableContractDebit> = [
        ...TABLE_COLUMNS,
        {
            title: descriptions.TABLE_COLUMNS.ACTIONS,
            dataIndex: ColumnsKeys.ACTIONS,
            key: ColumnsKeys.ACTIONS,
            align: searchableTableUtils.alignment.centerAlign,
            render: (_, record: PayableContractDebit) => {
                const handleCreatePaymentClick = (contractId: number) => {
                    dispatch(getPayableDebitByIdApi(contractId))
                }

                const handleDeleteRecord = (payableContractDebitId: number) => {
                    setSelectedAction("DELETE_PCD")
                    setRefId(payableContractDebitId.toString())
                    dispatch(createAuthTokenApi({
                        purpose: "DELETE_PCD",
                        refId: payableContractDebitId.toString(),
                    }))
                }

                return <Space size="middle">
                    <Tooltip
                        title={commonDescriptions.ACTIONS.CREATE_PAYMENT}
                    >
                        <Button type="primary" ghost shape="circle" size="small"
                            icon={<CreditCardOutlined />}
                            onClick={() => handleCreatePaymentClick(record.id!)}
                        />
                    </Tooltip>
                    <Tooltip
                        title={commonDescriptions.ACTIONS.DELETE}
                    >
                        <Button type="primary" danger ghost shape="circle" size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteRecord(record.id!)}
                        />
                    </Tooltip>
                </Space>
            },
        },
    ]

    const renderLoader = () => (
        <Loader
            isVisible={reduxProps.isGettingContractDebit || reduxProps.isCreatingAuthToken}
            title={descriptions.MAIN_ACTION_TITLE}
            description={descriptions.MAIN_ACTION_DESCRIPTION}
        />
    )

    const closeModal = () => {
        dispatch(cleanCurrentPayableDebit())
    }

    const openNotification = (message: string, description: string) => {
        notification.success({
            message,
            description,
        });
    };

    const onValidateAuthTokenSuccess = (authTokenRequest: AuthTokenRequest) => {
        setTokenValidatorOpen(false)
        setSelectedAction("DELETE_PCD")
        const foundPayableContract = reduxProps.payableContractDebits?.find(pcd => pcd.id === Number(authTokenRequest.refId))
        if (foundPayableContract) {
            setPayableContractToDelete(foundPayableContract)
        }
        // TODO: ADD HERE THE FORM FOR ANULATION
        // navigate(Urls.FRONTEND.SALE.ABOLISH_SALE_PAGE(
        //     Number(authTokenRequest.refId),
        //     reduxProps.pageToken!,
        // ))
    }

    return (
        <>
            <PageCard
                size="small"
                title={descriptions.TITLE}
            >
                <SearchableTable
                    showSearch
                    showLoader={reduxProps.isGettingContractDebits}
                    items={reduxProps.payableContractDebits || []}
                    tableColumns={tblColumns as any}
                    searchLabel={descriptions.TABLE.SEARCH_PLACEHOLDER}
                    summaryColumns={summaryColumns}
                />
                {
                    reduxProps.currentPaymentDebit &&
                    <CreatePayableContractPayment
                        open={!!reduxProps.currentPaymentDebit}
                        onCancel={() => { closeModal() }}
                        onAccept={() => {
                            closeModal()
                            initData()
                            openNotification(
                                createDescriptions.SUCCESS_MODAL.TITLE,
                                createDescriptions.SUCCESS_MODAL.DESCRIPTION,
                            )
                        }}
                        payableDebit={reduxProps.currentPaymentDebit}
                    />
                }
                {renderLoader()}
            </PageCard>
            {
                selectedAction && refId &&
                <AuthTokenValidator
                    open={tokenValidatorOpen}
                    onCancel={() => {
                        setTokenValidatorOpen(false)
                    }}
                    onAccept={onValidateAuthTokenSuccess}
                    purpose={selectedAction}
                    refId={refId}
                />
            }
            {
                payableContractToDelete &&
                <ContractDebitDelete
                    open={true}
                    onCancel={() => { setPayableContractToDelete(undefined) }}
                    onAccept={() => {
                        setPayableContractToDelete(undefined)
                        initData()
                        openNotification(
                            deleteDescriptions.SUCCESS_MODAL.TITLE,
                            deleteDescriptions.SUCCESS_MODAL.DESCRIPTION,
                        )
                    }}
                    type={"PAYABLE_CONTRACT"}
                    contractDebit={payableContractToDelete}
                />
            }
        </>
    )
}
